/**
 * Document Content Validator and Sanitizer
 *
 * Moved from `commands4/utils/validateDocContent.ts` into the public validators API.
 */
import { createFileLogger } from '../../../../../utils/logger';
import { traceLog } from '../../../../../services/debug/traceHelpers';

const log = createFileLogger('VALIDATE_DOC_CONTENT', { enabled: false });

// Derived from `schema.ts` to prevent circular dependencies
const VALID_NODE_TYPES = new Set([
  'doc', 'paragraph', 'blockquote', 'horizontal_rule', 'heading',
  'code_block', 'ordered_list', 'bullet_list', 'list_item',
  'table', 'table_row', 'table_cell', 'table_header',
  'text', 'image', 'hard_break'
]);

// Aliases we accept from tools/LLMs and normalize into canonical schema
const TYPE_ALIASES: Record<string, (node: any) => any> = {
  heading1: (n) => ({ type: 'heading', attrs: { level: 1 }, text: n.text ?? '' }),
  heading2: (n) => ({ type: 'heading', attrs: { level: 2 }, text: n.text ?? '' }),
  heading3: (n) => ({ type: 'heading', attrs: { level: 3 }, text: n.text ?? '' }),
  heading4: (n) => ({ type: 'heading', attrs: { level: 4 }, text: n.text ?? '' }),
  heading5: (n) => ({ type: 'heading', attrs: { level: 5 }, text: n.text ?? '' }),
  heading6: (n) => ({ type: 'heading', attrs: { level: 6 }, text: n.text ?? '' }),
  orderedList: (n) => ({ type: 'ordered_list', content: normalizeListItems(n.items ?? n.content) }),
  bulletList: (n) => ({ type: 'bullet_list', content: normalizeListItems(n.items ?? n.content) }),
  listItem: (n) => ({ type: 'list_item', content: ensureArray(n.content ?? (n.text ? [{ type: 'paragraph', text: n.text }] : [])) }),
};

function ensureArray(val: any): any[] { return Array.isArray(val) ? val : (val == null ? [] : [val]); }

function normalizeListItems(items: any): any[] {
  const arr = ensureArray(items);
  return arr.map((it) => {
    if (!it) return null;
    if (it.type === 'listItem' || it.type === 'list_item') {
      return TYPE_ALIASES.listItem ? TYPE_ALIASES.listItem(it) : it;
    }
    // Wrap loose paragraphs into list_item
    if (it.type === 'paragraph' || it.text) {
      const textVal = it.text ?? (it.content?.[0]?.text ?? '');
      if (!textVal || String(textVal).trim() === '') return null;
      return { type: 'list_item', content: [{ type: 'paragraph', text: String(textVal) }] };
    }
    // General case: sanitize children and drop empty bullets
    const children = validateAndSanitizeNodes(ensureArray(it.content ?? []), 'list_item.content', []);
    const hasContent = children.length > 0;
    if (!hasContent) return null;
    return { type: 'list_item', content: children };
  }).filter(Boolean);
}

// --- Table normalization helpers ---
function normalizeCellToCanonical(cell: any): any {
  if (cell == null) return null;
  if (typeof cell !== 'object') {
    return { type: 'table_cell', text: String(cell) };
  }
  let type = cell.type;
  if (!type) {
    type = cell.header ? 'table_header' : 'table_cell';
  }
  if (type === 'tableHeader') type = 'table_header';
  if (type === 'tableCell') type = 'table_cell';
  const out: any = { ...cell, type };
  // Ensure content shape if provided; allow text-only to be wrapped later
  if (out.cells) delete out.cells;
  return out;
}

function normalizeRowToCanonical(row: any): any {
  if (!row || typeof row !== 'object') {
    return { type: 'table_row', content: [] };
  }
  const cells = ensureArray(row.cells ?? row.content).map(normalizeCellToCanonical).filter(Boolean);
  return { type: 'table_row', content: cells };
}

function normalizeTableToCanonical(table: any): any {
  if (!table || typeof table !== 'object') {
    return { type: 'table', content: [] };
  }
  let contentCandidate: any = table.rows ?? table.content;
  // Unwrap nested { type: 'table', content: [...] }
  if (contentCandidate && typeof contentCandidate === 'object' && !Array.isArray(contentCandidate) && contentCandidate.type === 'table') {
    contentCandidate = contentCandidate.content;
  }
  const rows = ensureArray(contentCandidate).map(normalizeRowToCanonical).filter(Boolean);
  return { type: 'table', content: rows };
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedContent: any[];
}

export function validateAndSanitizeDocContent(content: any[]): ValidationResult {
  const errors: string[] = [];
  
  // 🔍 QUOTE ENCODING TRACE: Check content before validation (avoid JSON.stringify!)
  const checkForQuotes = (obj: any): {hasQuotes: boolean; sampleText: string} => {
    if (typeof obj === 'string') {
      return {hasQuotes: /["']/.test(obj), sampleText: obj.substring(0, 100)};
    }
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const result = checkForQuotes(item);
        if (result.hasQuotes) return result;
      }
    }
    if (obj && typeof obj === 'object') {
      if (obj.text && typeof obj.text === 'string') {
        return {hasQuotes: /["']/.test(obj.text), sampleText: obj.text.substring(0, 100)};
      }
      for (const [key, value] of Object.entries(obj)) {
        const result = checkForQuotes(value);
        if (result.hasQuotes) return result;
      }
    }
    return {hasQuotes: false, sampleText: ''};
  };

  const quoteCheck = checkForQuotes(content);
  
  if (!content || !Array.isArray(content)) {
    return {
      isValid: false,
      errors: ['Content must be a non-empty array.'],
      sanitizedContent: [],
    };
  }

  // Guard: if the first element is a single string that appears to be quoted JSON,
  // unwrap or fail fast so we never insert raw JSON as paragraph text.
  const looksLikeJsonString = (s: string): boolean => {
    const t = s.trim();
    // Heuristics: starts with '[' or '{' and contains a "type" field somewhere
    return (t.startsWith('[') || t.startsWith('{')) && /"type"\s*:\s*"[a-zA-Z_]+"/.test(t);
  };
  const tryParseEmbeddedJson = (s: string): any | undefined => {
    const src = s.trim();
    const attempt = (x: string) => { try { return JSON.parse(x); } catch { return undefined; } };
    // Direct parse
    let parsed = attempt(src);
    if (parsed) return parsed;
    // Slice between first '[' and last ']' (handles decorated multi-line strings)
    const lb = src.indexOf('[');
    const rb = src.lastIndexOf(']');
    if (lb !== -1 && rb !== -1 && rb > lb) {
      parsed = attempt(src.slice(lb, rb + 1));
      if (parsed) return parsed;
    }
    // Slice between first '{' and last '}' as a fallback
    const lc = src.indexOf('{');
    const rc = src.lastIndexOf('}');
    if (lc !== -1 && rc !== -1 && rc > lc) {
      parsed = attempt(src.slice(lc, rc + 1));
      if (parsed) return parsed;
    }
    return undefined;
  };

  if (content.length === 1 && typeof content[0] === 'string' && looksLikeJsonString(content[0] as string)) {
    const parsed = tryParseEmbeddedJson(content[0] as string);
    if (Array.isArray(parsed)) {
      content = parsed as any[];
    } else {
      return {
        isValid: false,
        errors: ['Content must be a JSON array, not a quoted string. Please send a valid array of nodes.'],
        sanitizedContent: []
      };
    }
  }

  // NEW: Expand or block embedded JSON-like payloads that slipped inside
  // arrays (e.g., a paragraph.text that contains a JSON array string). This
  // prevents inserting raw JSON text when upstream parsing failed.
  const expandEmbeddedJsonPayload = (raw: any[]): any[] => {
    const out: any[] = [];
    for (const item of raw) {
      // Case 1: a bare string that looks like a JSON node/array
      if (typeof item === 'string' && looksLikeJsonString(item)) {
        const parsed = tryParseEmbeddedJson(item);
        if (Array.isArray(parsed)) {
          out.push(...parsed);
          continue;
        }
        errors.push('Embedded JSON-like string detected but could not be parsed into nodes.');
        // Drop the raw JSON-looking string; do NOT insert as visible text
        continue;
      }
      // Case 2: object whose text contains JSON-like array (common: paragraph.text)
      if (item && typeof item === 'object') {
        const maybeText = (item as any).text;
        if (typeof maybeText === 'string' && looksLikeJsonString(maybeText)) {
          const parsed = tryParseEmbeddedJson(maybeText);
          if (Array.isArray(parsed)) {
            out.push(...parsed);
            continue;
          }
          errors.push('Paragraph text contains JSON-like content that could not be parsed into nodes.');
          // Skip inserting raw JSON-looking text
          continue;
        }
        // Case 3: object whose content is a JSON string
        const maybeContent = (item as any).content;
        if (typeof maybeContent === 'string' && looksLikeJsonString(maybeContent)) {
          const parsed = tryParseEmbeddedJson(maybeContent);
          if (Array.isArray(parsed)) {
            out.push(...parsed);
            continue;
          }
          errors.push('Node.content is a JSON-like string that could not be parsed into nodes.');
          continue;
        }
      }
      out.push(item);
    }
    return out;
  };

  content = expandEmbeddedJsonPayload(content);

  log.log(`[validateDocContent] Starting validation and sanitization for ${content.length} nodes.`);
  
  // Pre-pass: normalize markdown-like blocks (headings, bullets) and inline **bold**
  const preGrouped = groupMarkdownBlocks(content);
  
  const preNormalized = preGrouped.map((node) => normalizeNodeAliases(node));

  // Helper: detect if a paragraph has any visible inline text, even if top-level text is empty
  const paragraphHasText = (node: any): boolean => {
    if (!node || node.type !== 'paragraph') return false;
    if (typeof node.text === 'string' && node.text.trim().length > 0) return true;
    const extractText = (n: any): string => {
      if (!n) return '';
      if (typeof n.text === 'string') return n.text;
      if (Array.isArray(n.content)) return n.content.map(extractText).join('');
      return '';
    };
    const inline = Array.isArray(node.content) ? extractText({ content: node.content }) : '';
    return inline.trim().length > 0;
  };

  const sanitizedBeforeFilter = validateAndSanitizeNodes(preNormalized, 'root', errors);
  const droppedParagraphs = sanitizedBeforeFilter.filter(
    (n) => n?.type === 'paragraph' && !paragraphHasText(n)
  ).length;

  // Drop only paragraphs that have neither top-level text nor any inline content text
  const sanitizedContent = sanitizedBeforeFilter.filter(
    (n) => !(n?.type === 'paragraph' && !paragraphHasText(n))
  );

  // 🔍 QUOTE ENCODING TRACE: Check content AFTER validation
  const quoteCheckAfter = checkForQuotes(sanitizedContent);
  if (quoteCheck.hasQuotes || quoteCheckAfter.hasQuotes) {
    console.log('🔍 [VALIDATION_QUOTES_TRACE] Content AFTER validation:', {
      beforeSampleText: quoteCheck.sampleText,
      afterSampleText: quoteCheckAfter.sampleText,
      hadQuotesBefore: quoteCheck.hasQuotes,
      hasQuotesAfter: quoteCheckAfter.hasQuotes,
      entitiesAfter: (quoteCheckAfter.sampleText.match(/&[a-zA-Z0-9#]+;/g) || []).length,
      ENCODING_DETECTED: quoteCheck.hasQuotes && !quoteCheckAfter.hasQuotes,
      SOURCE: 'validateAndSanitizeDocContent_AFTER'
    });
  }

  const isValid = errors.length === 0;
  if (!isValid) {
    log.warn(`[validateDocContent] Validation failed with ${errors.length} errors:`, errors);
  } else {
    log.log(`[validateDocContent] Validation and sanitization successful.`);
  }

  // Trace summary for validator
  try {
    traceLog({
      traceId: undefined,
      type: 'doc_validate_summary',
      data: {
        inputCount: Array.isArray(content) ? content.length : 0,
        sanitizedCount: sanitizedContent.length,
        droppedParagraphs,
      }
    });
  } catch {}

  return { isValid, errors, sanitizedContent };
}

function validateAndSanitizeNodes(nodes: any[], path: string, errors: string[]): any[] {
  return nodes.map((originalItem, index) => {
    const currentPath = `${path}[${index}]`;
    
    // Allow primitives by coercing to paragraph text
    if (typeof originalItem === 'string' || typeof originalItem === 'number' || typeof originalItem === 'boolean') {
      originalItem = { type: 'paragraph', text: String(originalItem) };
    }
    let item = originalItem ? { ...originalItem } : null;

    if (!item || typeof item !== 'object') {
      errors.push(`Content item at ${currentPath} is not a valid object.`);
      return null;
    }

    // 1. Normalize aliases again if any slipped through
    if (item && typeof item.type === 'string' && TYPE_ALIASES[item.type]) {
      item = normalizeNodeAliases(item);
    }
    // 2. Validate and sanitize the 'type' property itself
    if (!item.type || typeof item.type !== 'string' || !VALID_NODE_TYPES.has(item.type)) {
      // If still not valid, attempt markdown-style coercions
      const coerced = coerceMarkdownLikeNode(item);
      if (coerced && VALID_NODE_TYPES.has(coerced.type)) {
        item = coerced;
      } else {
        errors.push(`Content item at ${currentPath} has a missing or invalid type: '${item.type}'.`);
        return null;
      }
    }
    
    // 2. Schema-aware sanitization and validation
    const sanitizeTextToBlockContent = (node: any, nodePath: string) => {
      if (node.text && typeof node.text === 'string' && (!node.content || node.content.length === 0)) {
        log.log(`[Sanitize] Auto-fixing '${node.type}' at ${nodePath} by wrapping 'text' in a 'paragraph'.`);
        node.content = [{ type: 'paragraph', text: node.text }];
        delete node.text;
      }
    };
    
    switch (item.type) {
      case 'blockquote':
      case 'list_item':
      case 'table_cell':
      case 'table_header':
        sanitizeTextToBlockContent(item, currentPath);
        if (!item.content || !Array.isArray(item.content)) {
          errors.push(`Node at ${currentPath} (type: ${item.type}) must have a 'content' array.`);
        } else {
          // Coerce any primitive children to paragraphs during recursion
          item.content = validateAndSanitizeNodes(item.content, `${currentPath}.content`, errors)
            .map((child) => {
              if (!child || typeof child !== 'object') return null;
              // Ensure cells contain paragraphs only; convert text nodes to paragraphs
              if (item.type === 'table_cell' || item.type === 'table_header') {
                if (child.type === 'text' && typeof child.text === 'string') {
                  return { type: 'paragraph', text: child.text };
                }
                if (child.type !== 'paragraph') {
                  // Best-effort: wrap unknown types into paragraph text
                  const txt = typeof (child as any).text === 'string' ? (child as any).text : '';
                  return { type: 'paragraph', text: txt };
                }
              }
              return child;
            })
            .filter(Boolean);
          // Drop empty list_item after sanitization
          if (item.type === 'list_item') {
            const safeHasText = (n: any) => n?.type === 'paragraph' && typeof n.text === 'string' && n.text.trim().length > 0;
            const safeNestedHasText = (n: any) => n?.type === 'list_item' && Array.isArray(n.content) && n.content.some((sub: any) => sub?.type === 'paragraph' && typeof sub.text === 'string' && sub.text.trim().length > 0);
            const visible = Array.isArray(item.content) && item.content.some((c: any) => safeHasText(c) || safeNestedHasText(c));
            if (!visible) return null as any;
          }
        }
        break;

      case 'ordered_list':
      case 'bullet_list':
      case 'table':
      case 'table_row':
        if (!item.content || !Array.isArray(item.content)) {
          errors.push(`Node at ${currentPath} (type: ${item.type}) must have a 'content' array.`);
        } else {
          const sanitized = validateAndSanitizeNodes(item.content, `${currentPath}.content`, errors);
          item.content = sanitized;
        }
        break;

      case 'image':
        if (!item.src && !item.attrs?.src) {
          errors.push(`Node at ${currentPath} (type: image) is missing 'src' or 'attrs.src' property.`);
        }
        break;

      case 'horizontal_rule':
      case 'hard_break':
        break;
        
      case 'text':
        if (typeof item.text !== 'string') {
          errors.push(`Node at ${currentPath} (type: text) must have a 'text' property of type string.`);
        }
        break;

      default: // Handles paragraph, heading
        if (item.content && Array.isArray(item.content)) {
          // This node has inline content; sanitize its children.
          item.content = validateAndSanitizeNodes(item.content, `${currentPath}.content`, errors);
        } else if (typeof item.text !== 'string') {
          // This is a simple text-based node, but text is missing or invalid.
          item.text = ''; // Sanitize by adding an empty text string.
          log.log(`[Sanitize] Fixed missing or invalid 'text' property on node of type '${item.type}' at ${currentPath}.`);
        }
        break;
    }
    return item;
  }).filter(item => item !== null);
}

// --- Markdown-like pre-pass helpers ---
function parseBoldInlineToMarks(text: string): any[] {
  if (typeof text !== 'string') return [{ type: 'text', text: String(text) }];
  if (!text.includes('**')) return [{ type: 'text', text }];
  const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);
  const out: any[] = [];
  for (const part of parts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      out.push({ type: 'text', text: part.slice(2, -2), marks: [{ type: 'bold' }] });
    } else {
      out.push({ type: 'text', text: part });
    }
  }
  return out;
}

function getBulletMarker(text: string): 'bullet' | null {
  const s = (text || '').trimStart();
  if (s.startsWith('* ')) return 'bullet';
  return null;
}

function stripBulletMarker(text: string): string {
  const s = (text || '').trimStart();
  if (s.startsWith('* ')) return s.slice(2);
  return text || '';
}

function matchHeading(text: string): { level: number; rest: string } | null {
  const m = (text || '').match(/^(#{1,6})\s+(.*)$/);
  if (!m) return null;
  return { level: Math.min(6, m[1].length), rest: m[2] };
}

function groupMarkdownBlocks(raw: any[]): any[] {
  if (!Array.isArray(raw)) return raw as any;
  const out: any[] = [];
  let bulletItems: any[] | null = null;

  const flushBullets = () => {
    if (bulletItems && bulletItems.length > 0) {
      out.push({ 
        type: 'bullet_list', 
        content: bulletItems 
      });
    }
    bulletItems = null;
  };

  for (const node of raw) {
    const item = node && typeof node === 'object' ? { ...node } : node;
    // Only transform simple paragraph.text cases here
    if (item && item.type === 'paragraph' && typeof item.text === 'string') {
      const txt = item.text;
      // Heading markdown
      const hm = matchHeading(txt);
      if (hm) {
        flushBullets();
        out.push({ type: 'heading', attrs: { level: hm.level }, content: parseBoldInlineToMarks(hm.rest) });
        continue;
      }
      // Bullet line: create/append list_item
      const bulletMarker = getBulletMarker(txt);
      if (bulletMarker) {
        const stripped = stripBulletMarker(txt);
        const paragraph = { type: 'paragraph', content: parseBoldInlineToMarks(stripped) };
        const listItem = { type: 'list_item', content: [paragraph] };
        
        if (!bulletItems) {
          bulletItems = [];
        }
        bulletItems.push(listItem);
        continue;
      }
      // Regular paragraph: convert inline **bold** to marks
      flushBullets();
      out.push({ type: 'paragraph', content: parseBoldInlineToMarks(txt) });
      continue;
    }

    // For any non-paragraph or already structured nodes, flush bullets and push as-is
    flushBullets();
    out.push(item);
  }

  // Flush any remaining bullets at the end
  flushBullets();
  return out;
}

function normalizeNodeAliases(node: any): any {
  if (!node || typeof node !== 'object') return node;
  const t = node.type;
  // Coerce simple content:string → text for paragraph/heading
  if ((t === 'paragraph' || t === 'heading') && typeof node.content === 'string' && (node.text == null)) {
    node = { ...node, text: node.content };
    delete node.content;
  }
  // If heading has level (number) but no attrs, normalize
  if (t === 'heading' && typeof (node as any).level === 'number' && !node.attrs) {
    node = { ...node, attrs: { level: (node as any).level } };
    delete (node as any).level;
  }
  // Normalize table structures from simplified shapes
  if (t === 'table') {
    return normalizeTableToCanonical(node);
  }
  if (t === 'table_row') {
    return normalizeRowToCanonical(node);
  }
  if (t && TYPE_ALIASES[t]) {
    return TYPE_ALIASES[t](node);
  }
  // Fix common LLM shape: canonical 'bullet_list'/'ordered_list' but using 'items' instead of 'content'
  if ((t === 'bullet_list' || t === 'ordered_list') && (!Array.isArray(node.content))) {
    const itemsCandidate = (node as any).items ?? (node as any).children ?? (node as any).list ?? [];
    return { type: t, content: normalizeListItems(itemsCandidate) };
  }
  // Fix list_item with text only or missing content
  if (t === 'list_item' && (!Array.isArray((node as any).content))) {
    const textVal = (node as any).text;
    if (typeof textVal === 'string') {
      return { type: 'list_item', content: [{ type: 'paragraph', text: textVal }] };
    }
    // If there is a single paragraph-like child under a different key, coerce it
    const itemsCandidate = (node as any).items ?? (node as any).children ?? (node as any).content;
    if (itemsCandidate) {
      return { type: 'list_item', content: validateAndSanitizeNodes(ensureArray(itemsCandidate), 'list_item.content', []) };
    }
    return { type: 'list_item', content: [{ type: 'paragraph', text: '' }] };
  }
  // Convert markdown-style headings in paragraph text
  if (t === 'paragraph' && typeof node.text === 'string') {
    const md = node.text.trim();
    const m = /^(#{1,6})\s+(.*)$/.exec(md);
    if (m) {
      const level = Math.min(m[1].length, 6);
      return { type: 'heading', attrs: { level }, text: m[2] };
    }
    // Convert bullet paragraphs to list structures opportunistically later
  }
  // Normalize bullet/ordered list containers with items property
  if ((t === 'bulletList' || t === 'orderedList') && (node.items || node.content)) {
    return TYPE_ALIASES[t](node);
  }
  // Normalize listItem alias
  if (t === 'listItem') {
    return TYPE_ALIASES.listItem(node);
  }
  // Horizontal rule markdown ---
  if (t === 'paragraph' && typeof node.text === 'string' && node.text.trim() === '---') {
    return { type: 'horizontal_rule' };
  }
  // Bullet paragraph like "• text" or "- text" → keep as paragraph; a later pass could group into lists.
  return node;
}

function coerceMarkdownLikeNode(node: any): any | null {
  if (!node) return null;
  if (typeof node.text === 'string') {
    const trimmed = node.text.trim();
    if (/^(#{1,6})\s+/.test(trimmed)) {
      const m = /^(#{1,6})\s+(.*)$/.exec(trimmed)!;
      return { type: 'heading', attrs: { level: m[1].length }, text: m[2] };
    }
    if (trimmed === '---') return { type: 'horizontal_rule' };
  }
  // Fallback: treat unknown node as paragraph
  return { type: 'paragraph', text: typeof node.text === 'string' ? node.text : '' };
}


